// Single source of truth for the GIS equipment catalog.
// Consumed by:
//   - app/[locale]/equipment/[brand]/[product]/page.tsx  (dedicated product pages)
//   - app/[locale]/brands/<slug>/page.tsx                (brand overviews)
//   - app/[locale]/page.tsx                              (homepage equipment grid)
//   - components/layout/public-footer.tsx                (footer equipment links)
//   - app/sitemap.ts                                     (indexable URLs)
// Keeping this file authoritative prevents the prior "split-brain" where the
// brand pages and the equipment/[category] page each maintained their own copy
// of the same product data and drifted out of sync.

export type BrandSlug = 'wacker-neuson' | 'hyundai-everdigm';

export type SpecTable = { head: string[]; rows: string[][] };

export type ProductDoc = { label: string; href: string };

export type Product = {
  brand: BrandSlug;
  slug: string;
  name: string;          // display name on the dedicated page and brand card (e.g. "Wheel Loaders (Tier 3)")
  shortName: string;     // compact label for homepage tile + footer (e.g. "Wheel Loaders")
  blurb: string;         // 1-line summary shown on cards
  image?: string;
  photo?: boolean;       // when true, image uses the photo-treated card frame
  features: string[];
  specTable?: SpecTable;
  docs: ProductDoc[];
  // SEO + dedicated-page rendering:
  h1Lead: string;        // first part of the H1 (e.g. "Wheel Loaders in")
  h1Highlight: string;   // highlighted second part of the H1 (e.g. "Guyana")
  subtitle: string;
  intro: string;
  seoTitle: string;
  seoDescription: string;
  // Homepage tile enrichment:
  tileModelChip: string; // small text under the tile title (e.g. "WL25 · WL28")
  tileBadges: string[];  // 2-3 short spec badges (e.g. ["Tier 3", "No DPF"])
  // Brand cross-link siblings to surface on the dedicated page (slugs within same brand):
  related?: string[];
};

export const BRANDS: Record<BrandSlug, { name: string; tagline: string; logo?: string; href: string }> = {
  'wacker-neuson': {
    name: 'Wacker Neuson',
    tagline: 'Authorized Distributor for Guyana',
    logo: '/images/brands/wacker-neuson-logo.svg',
    href: '/brands/wacker-neuson',
  },
  'hyundai-everdigm': {
    name: 'Hyundai Everdigm',
    tagline: 'Excavator attachments partner',
    logo: '/images/brands/hyundai-everdigm-logo.png',
    href: '/brands/hyundai-everdigm',
  },
};

export const PRODUCTS: Record<BrandSlug, Product[]> = {
  'wacker-neuson': [
    {
      brand: 'wacker-neuson',
      slug: 'mini-excavators',
      name: 'Mini Excavators (0.9–2.9 t · Tier 3)',
      shortName: 'Mini Excavators',
      blurb: 'Compact and zero-tail-swing mini excavators from 0.9 to 2.9 tonnes — China-built Tier 3, sized for tight access and fines-recovery work.',
      image: 'wacker-neuson-mini-excavator.webp',
      photo: true,
      features: [
        'Tier 3 engines — no DPF, run on standard diesel',
        'Zero tail swing (EZ) for confined sites',
        'Dozer blade, LED work lights, auxiliary hydraulics',
        'Telescopic undercarriage on EZ17CN-2 / EZ20CN-2',
      ],
      specTable: {
        head: ['Model', 'Operating weight', 'Engine', 'Emissions'],
        rows: [
          ['ET08CN', '0.9 t', 'Yanmar 3TNV70 · 9.9 kW', 'Tier 3'],
          ['EZ17CN-2', '1.7 t', 'Yanmar 3TNV76 · 13.8 kW', 'Tier 3'],
          ['EZ20CN-2', '1.9 t', 'Yanmar 3TNV76 · 13.8 kW', 'Tier 3'],
          ['EZ28CN', '2.9 t', 'Yanmar 3TNV76 · 15.8 kW', 'Tier 3'],
        ],
      },
      docs: [
        { label: 'EZ17CN-2 spec sheet', href: '/line-sheets/wacker-neuson-ez17-mini-excavator.pdf' },
        { label: 'EZ28CN spec sheet', href: '/line-sheets/wacker-neuson-ez28-mini-excavator.pdf' },
      ],
      h1Lead: 'Mini Excavators in',
      h1Highlight: 'Guyana',
      subtitle:
        'Compact and zero-tail-swing mini excavators from 0.9 to 2.9 tonnes — Tier 3, no DPF, running on standard diesel. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson mini excavators across Guyana for tight-access earthworks, trenching, foundations and fines-recovery work. The 0.9–2.9 t range is China-built Tier 3 with no diesel particulate filter (DPF), so it runs on standard diesel — well suited to interior and off-grid sites where ultra-low-sulfur diesel is not always available. Zero-tail-swing EZ models work in confined urban and camp footprints; the ET08CN brings proper dig depth in a machine that fits through a gateway. We match the right size to your job and source it through Wacker Neuson, handling freight, customs and after-sales support, so you get the machine you need without carrying the cost of a standing fleet.',
      seoTitle: 'Mini Excavators in Guyana — 0.9–2.9 t Tier 3 (No DPF) | Goldman Industrial Solutions',
      seoDescription:
        'Mini excavators in Guyana through Goldman Industrial Solutions — Wacker Neuson ET/EZ models from 0.9 to 2.9 tonnes, Tier 3 with no DPF (run on standard diesel), sourced to order with delivery and local support. Request a quote.',
      tileModelChip: 'ET08 · EZ17 · EZ20 · EZ28',
      tileBadges: ['Tier 3', 'No DPF', '0.9–2.9 t'],
      related: ['compact-excavators', 'wheel-loaders'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'compact-excavators',
      name: 'Compact Excavators (6–7 t)',
      shortName: 'Compact Excavators',
      blurb: 'Cab-class conventional excavators at 6 and 7 tonnes for heavier earthworks — air-conditioned, China IV (DOC, no DPF), on standard diesel.',
      image: 'wacker-neuson-compact-excavator.webp',
      photo: true,
      features: [
        'China IV with DOC — no DPF, runs on standard diesel',
        'Air-conditioned cab, 3.5" multifunction display',
        'Load-sensing LUDV hydraulics, variable pump 144 l/min',
        'Steel tracks, dozer blade, LED work lights',
      ],
      specTable: {
        head: ['Model', 'Operating weight', 'Engine', 'Tracks'],
        rows: [
          ['ET66CN-GB4', '6 t', 'Yanmar 4TNV94L · 35.9 kW', '400 mm steel'],
          ['ET75CN-GB4', '7 t', 'Yanmar 4TNV94L · 35.9 kW', '450 mm steel'],
        ],
      },
      docs: [
        { label: 'ET66CN-GB4 spec sheet', href: '/line-sheets/wacker-neuson-et66-excavator.pdf' },
        { label: 'ET75CN-GB4 spec sheet', href: '/line-sheets/wacker-neuson-et75-excavator.pdf' },
      ],
      h1Lead: 'Compact Excavators in',
      h1Highlight: 'Guyana',
      subtitle:
        'Air-conditioned cab-class conventional excavators at 6 and 7 tonnes for heavier earthworks — China IV (DOC, no DPF), running on standard diesel. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson compact excavators across Guyana for heavier earthworks, loading trucks, foundation excavation and site clearance where a mini is too small. The 6-tonne ET66CN and 7-tonne ET75CN bring a proper air-conditioned cab, steel tracks and load-sensing LUDV hydraulics — comfortable for long shifts and productive on hard ground. They are China IV with a Diesel Oxidation Catalyst (DOC) but no diesel particulate filter, so they still run on standard diesel. We match the right configuration to your job and source it through Wacker Neuson, handling freight, customs and after-sales support locally.',
      seoTitle: 'Compact Excavators in Guyana — 6–7 t Cab Class, No DPF | Goldman Industrial Solutions',
      seoDescription:
        'Compact excavators in Guyana through Goldman Industrial Solutions — Wacker Neuson ET66CN and ET75CN at 6 and 7 tonnes, air-conditioned cab, China IV (DOC, no DPF) running on standard diesel. Sourced to order. Request a quote.',
      tileModelChip: 'ET66CN · ET75CN',
      tileBadges: ['6–7 t', 'Cab + A/C', 'No DPF'],
      related: ['mini-excavators', 'wheel-loaders'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'wheel-loaders',
      name: 'Wheel Loaders (Tier 3)',
      shortName: 'Wheel Loaders',
      blurb: 'Articulated compact wheel loaders — 0.3 to 0.42 m³ buckets, Tier 3, for loading, stockpiling and yard work.',
      image: 'wacker-neuson-wheel-loader.webp',
      photo: true,
      features: [
        'Tier 3 engines — no DPF, standard diesel',
        'Articulated, cabbed compact wheel loaders',
        'Quick-hitch plate for buckets and forks',
        'Maneuverable on tight and soft ground',
      ],
      specTable: {
        head: ['Model', 'Bucket', 'Tipping load', 'Engine'],
        rows: [
          ['WL25', '0.3 m³', '1,144–1,958 kg', 'Perkins 403J-17 · 18.4 kW · Tier 3'],
          ['WL28', '0.42 m³', '1,600–2,200 kg', 'Yanmar 3TNV80FT · 18.4 kW · Tier 3'],
        ],
      },
      docs: [
        { label: 'WL25 spec sheet', href: '/line-sheets/wacker-neuson-wl25-wheel-loader.pdf' },
        { label: 'WL28 spec sheet', href: '/line-sheets/wacker-neuson-wl28-wheel-loader.pdf' },
      ],
      h1Lead: 'Wheel Loaders in',
      h1Highlight: 'Guyana',
      subtitle:
        'Articulated compact wheel loaders that run on standard diesel — for loading, stockpiling and yard work, supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Tier 3 compact wheel loaders across Guyana for loading, stockpiling and yard work. They are articulated, cabbed machines with no diesel particulate filter, so they run on standard diesel — well suited to interior sites. The quick-hitch plate takes buckets and forks, and the articulated steering keeps them maneuverable on tight and soft ground. We source the right size through Wacker Neuson and handle delivery and after-sales support locally.',
      seoTitle: 'Wheel Loaders in Guyana — Tier 3 Compact WL25 / WL28 (No DPF) | Goldman Industrial Solutions',
      seoDescription:
        'Tier 3 articulated compact wheel loaders in Guyana through Goldman Industrial Solutions — Wacker Neuson WL25 and WL28 that run on standard diesel, sourced to order with delivery and local support. Request a quote.',
      tileModelChip: 'WL25 · WL28',
      tileBadges: ['Tier 3', 'No DPF', 'Articulated'],
      related: ['mini-excavators', 'compact-excavators'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'wheel-dumpers',
      name: 'Wheel Dumpers (Tier 3)',
      shortName: 'Site Dumpers',
      blurb: 'Four-wheel-drive site dumpers from 1 to 3 tonnes payload with swivelling skip — Tier 3, for moving material across rough ground.',
      image: 'wacker-neuson-dumper.webp',
      photo: true,
      features: [
        'Tier 3 engines — no DPF, standard diesel',
        'Permanent 4WD, hydrostatic Twin-lock drive',
        'Swivelling skip for precise discharge',
        '1 to 3 tonne payload for site haulage',
      ],
      specTable: {
        head: ['Model', 'Payload', 'Swivel skip', 'Engine'],
        rows: [
          ['DW10', '1,000 kg', '415–525 L', 'Yanmar · 18.9 kW · Tier 3'],
          ['DW15', '1,500 kg', '650–800 L', 'Yanmar · 18.9 kW · Tier 3'],
          ['DW20-2', '2,000 kg', '700–1,280 L', 'Yanmar · 18.9 kW · Tier 3'],
          ['DW30-2', '3,000 kg', '990–1,750 L', 'Yanmar · 18.9 kW · Tier 3'],
        ],
      },
      docs: [{ label: 'DW30-2 spec sheet', href: '/line-sheets/wacker-neuson-dw30-dumper.pdf' }],
      h1Lead: 'Site Dumpers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Four-wheel-drive site dumpers that run on standard diesel — for moving material across rough ground, supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Tier 3 wheel dumpers across Guyana for site haulage over rough and soft ground. They are permanent-4WD machines with a swivelling skip and no diesel particulate filter, so they run on standard diesel. The swivel skip lets the operator discharge precisely to either side without repositioning the machine, which keeps site traffic down and cycle times short. We source the right payload through Wacker Neuson — from 1-tonne DW10 up to 3-tonne DW30-2 — and handle delivery and after-sales support locally.',
      seoTitle: 'Site Dumpers in Guyana — Tier 3 4WD DW10–DW30 (No DPF) | Goldman Industrial Solutions',
      seoDescription:
        'Tier 3 four-wheel-drive site dumpers in Guyana through Goldman Industrial Solutions — Wacker Neuson DW10 to DW30 from 1 to 3 tonnes payload that run on standard diesel, sourced to order. Request a quote.',
      tileModelChip: 'DW10–DW30',
      tileBadges: ['Tier 3', '1–3 t payload', '4WD'],
      related: ['wheel-loaders', 'rollers'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'rollers',
      name: 'Ride-On Rollers (Tier 3)',
      shortName: 'Rollers',
      blurb: 'Ride-on double-drum vibratory rollers from 2.4 to 4.5 tonnes — Tier 3, for haul-road, pad and site compaction.',
      image: 'wacker-neuson-roller.webp',
      photo: true,
      features: [
        'Tier 3 engines — no DPF, standard diesel',
        'Ride-on double-drum vibratory rollers',
        'Standard or combined (drum + pad-foot) drums',
        '2.4 to 4.5 tonne class',
      ],
      specTable: {
        head: ['Model', 'Weight', 'Drum', 'Engine'],
        rows: [
          ['RD24-100', '2.4 t', '1,000 mm', 'Kubota · 22.9 kW · Tier 3'],
          ['RD27-120', '2.8 t', '1,200 mm', 'Kubota · 22.9 kW · Tier 3'],
          ['RD40-130', '4.0 t', '1,300 mm', 'Kubota · 34.6 kW · Tier 3'],
          ['RD45-140', '4.5 t', '1,400 mm', 'Kubota · 34.6 kW · Tier 3'],
        ],
      },
      docs: [
        { label: 'RD24-100 spec sheet', href: '/line-sheets/wacker-neuson-rd24-roller.pdf' },
        { label: 'RD40-130 spec sheet', href: '/line-sheets/wacker-neuson-rd40-roller.pdf' },
      ],
      h1Lead: 'Rollers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Ride-on double-drum vibratory rollers that run on standard diesel — for haul-road, pad and site compaction, supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Tier 3 ride-on rollers across Guyana for haul-road, pad and site compaction. They are double-drum vibratory machines with no diesel particulate filter, so they run on standard diesel. Standard and combined (drum + pad-foot) drums are available, which makes the same machine useful for both haul-road finishing and cohesive-soil pad work. We source the right size through Wacker Neuson and handle delivery and after-sales support locally.',
      seoTitle: 'Rollers in Guyana — Tier 3 Ride-On Double-Drum RD24–RD45 | Goldman Industrial Solutions',
      seoDescription:
        'Tier 3 ride-on double-drum vibratory rollers in Guyana through Goldman Industrial Solutions — Wacker Neuson RD24 to RD45 from 2.4 to 4.5 tonnes that run on standard diesel, sourced to order. Request a quote.',
      tileModelChip: 'RD24–RD45',
      tileBadges: ['Tier 3', '2.4–4.5 t', 'Double-drum'],
      related: ['wheel-dumpers', 'wheel-loaders'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'light-towers',
      name: 'Mobile Light Towers (LTT)',
      shortName: 'Light Towers',
      blurb: 'Diesel LED light towers for night operations, security lighting and extended-hours site work.',
      image: 'wacker-neuson-light-tower.webp',
      photo: true,
      features: [
        '4 × 320 W LED lamps — 192,000 lumens, wide maintenance-free coverage',
        'Fuel-efficient Kohler diesel — small engine, no DPF',
        '52.4 gal tank — up to 211 hrs runtime on lights only',
        'Photocell auto-start option',
      ],
      specTable: {
        head: ['Model', 'Prime output', 'LED lamps', 'Engine', 'Runtime (lights only)'],
        rows: [
          ['LTT4', '4 kW', '4 × 320 W', 'Kohler KDW702', '211 hrs'],
          ['LTT6', '6 kW', '4 × 320 W', 'Kohler KDW1003', '173 hrs'],
        ],
      },
      docs: [{ label: 'Light Tower line sheet', href: '/line-sheets/wacker-neuson-light-towers.pdf' }],
      h1Lead: 'Light Towers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Mobile diesel LED light towers for night operations, security lighting and extended-hours site work — supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies mobile light towers across Guyana for night operations, security lighting and extended-hours work. Wacker Neuson LED light towers give wide, bright, maintenance-free coverage on a fuel-efficient diesel engine — a single 52-gallon tank runs up to 211 hours on lights alone, and a photocell auto-start option makes them useful for unattended security lighting at remote camps and perimeter fencing. We source them to order and support them locally.',
      seoTitle: 'Light Towers in Guyana — Mobile LED LTT4 / LTT6 Diesel | Goldman Industrial Solutions',
      seoDescription:
        'Mobile diesel LED light towers in Guyana through Goldman Industrial Solutions — Wacker Neuson LTT4 and LTT6 for night operations and security lighting, sourced to order with delivery and support. Request a quote.',
      tileModelChip: 'LTT4 · LTT6',
      tileBadges: ['LED', 'Kohler diesel', '211 hrs runtime'],
      related: ['mobile-generators', 'portable-generators'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'mobile-generators',
      name: 'Mobile Generators (G14–G150)',
      shortName: 'Mobile Generators',
      blurb: 'Trailer-ready diesel generators from 13.5 to 151 kVA standby — sound-attenuated, single- or three-phase, for site power, standby and mining-camp duty.',
      image: 'wacker-neuson-mobile-generator.webp',
      photo: true,
      features: [
        'Kohler or Cummins diesel engines, sound-attenuated skid or trailer enclosure',
        'Prime 13.5–121 kW / standby 15–130 kW across 8 models',
        'G14–G50 run standard diesel, no DEF; G70–G150 use SCR with DEF',
        'Automatic Voltage Regulator (AVR) for clean, stable power',
      ],
      specTable: {
        head: ['Model', 'Prime output', 'Standby output', 'Engine', 'DEF/SCR'],
        rows: [
          ['G14', '13.5 kW / 13.5 kVA', '15 kW / 15 kVA', 'Kohler KSD1403NA', 'No'],
          ['G25', '20 kW / 25 kVA', '22 kW / 27 kVA', 'Kohler KDI1903', 'No'],
          ['G35', '28 kW / 35 kVA', '31 kW / 39 kVA', 'Kohler KDI1903', 'No'],
          ['G50', '38 kW / 48 kVA', '42 kW / 52 kVA', 'Kohler KDI2504', 'No'],
          ['G70', '58 kW / 72 kVA', '63 kW / 79 kVA', 'Kohler KDI3404', 'Yes'],
          ['G100', '80 kW / 100 kVA', '88 kW / 110 kVA', 'Cummins QSB5-G11', 'Yes'],
          ['G130', '104 kW / 130 kVA', '114 kW / 143 kVA', 'Cummins QSB5-G11', 'Yes'],
          ['G150', '121 kW / 151 kVA', '130 kW / 163 kVA', 'Cummins QSB5-G12', 'Yes'],
        ],
      },
      docs: [{ label: 'Mobile Generator line sheet', href: '/line-sheets/wacker-neuson-mobile-generators.pdf' }],
      h1Lead: 'Mobile Generators in',
      h1Highlight: 'Guyana',
      subtitle:
        'Trailer-ready diesel generators from 13.5 to 151 kVA standby — sound-attenuated, single- or three-phase, for site power, standby and mining-camp duty. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson mobile diesel generators across Guyana for site power, standby duty and mining-camp operations. The G14–G150 range runs from 13.5 kW prime up to 121 kW prime (151 kVA standby) across eight models, with Kohler engines on the smaller end and Cummins on the larger. The G14–G50 run on standard diesel with no DEF; the G70–G150 use SCR with DEF, so they need ultra-low-sulfur diesel where available. An Automatic Voltage Regulator (AVR) keeps power clean enough for sensitive electronics, and the sound-attenuated enclosure keeps noise down at camp sites. Tell us your load, your site and your fuel situation, and we will quote the right rating.',
      seoTitle: 'Mobile Generators in Guyana — Diesel G14–G150, 13.5–151 kVA | Goldman Industrial Solutions',
      seoDescription:
        'Mobile diesel generators in Guyana through Goldman Industrial Solutions — Wacker Neuson G14 to G150 from 13.5 to 151 kVA standby, Kohler/Cummins engines, sound-attenuated, sourced to order. Request a quote.',
      tileModelChip: 'G14–G150',
      tileBadges: ['13.5–151 kVA', 'Kohler/Cummins', 'AVR'],
      related: ['portable-generators', 'light-towers'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'portable-generators',
      name: 'Portable Generators',
      shortName: 'Portable Generators',
      blurb: 'Petrol portable generators for tools, lighting and smaller power needs on site.',
      image: 'wacker-neuson-portable-generator.webp',
      photo: true,
      features: [
        'Output 2,500–9,700 W (max)',
        '120 / 240 V, 60 Hz',
        'Honda petrol engines throughout the range',
        'Wheel kit on larger models, electric start on GPS9700A',
      ],
      specTable: {
        head: ['Model', 'Max output', 'Weight', 'Engine'],
        rows: [
          ['GP2500A', '2,500 W', '45 kg', 'Honda petrol'],
          ['GP4000A', '4,000 W', '75 kg', 'Honda petrol'],
          ['GP5500A', '5,500 W', '79 kg', 'Honda petrol'],
          ['GP7000A', '7,200 W', '79 kg', 'Honda petrol'],
          ['GPS9700A', '9,700 W', '190 kg', 'Honda petrol (2-cyl)'],
        ],
      },
      docs: [{ label: 'Portable Generator line sheet', href: '/line-sheets/wacker-neuson-portable-generators.pdf' }],
      h1Lead: 'Portable Generators in',
      h1Highlight: 'Guyana',
      subtitle:
        'Petrol portable generators from 2,500 to 9,700 W for tools, lighting and smaller power needs on site — Honda engines throughout, supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson portable petrol generators across Guyana for tools, lighting and smaller site loads. The five-model range runs from the 2.5 kW GP2500A up to the 9.7 kW GPS9700A, all on Honda petrol engines — wheel kits on the larger models and electric start on the GPS9700A make daily use straightforward. They suit construction sites, prospecting camps and events where a towable diesel genset is more machine than the job needs. We source them to order through Wacker Neuson and support them locally; for camp or standby power, see our Mobile Generators (G14–G150) range.',
      seoTitle: 'Portable Generators in Guyana — Petrol GP2500A–GPS9700A Honda | Goldman Industrial Solutions',
      seoDescription:
        'Petrol portable generators in Guyana through Goldman Industrial Solutions — Wacker Neuson GP2500A to GPS9700A (2.5–9.7 kW, Honda engines), sourced to order with delivery and support. Request a quote.',
      tileModelChip: 'GP2500A–GPS9700A',
      tileBadges: ['2.5–9.7 kW', 'Honda petrol', '120/240 V'],
      related: ['mobile-generators', 'light-towers'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'water-pumps',
      name: 'Water Pumps',
      shortName: 'Water Pumps',
      blurb: 'Self-priming trash, diaphragm and dewatering pumps for water transfer and mining-site duty.',
      image: 'wacker-neuson-water-pump.jpg',
      photo: true,
      features: [
        'PT trash pumps: 2", 3", 4" petrol, 6" diesel (PT6Y)',
        'PDT diaphragm pumps (2", 3") for mud and slurries',
        'PG dewatering pumps (2", 3") for clean-water removal',
        'Max discharge 189–4,300 L/min across all 8 models',
      ],
      specTable: {
        head: ['Model', 'Type', 'Size', 'Max discharge', 'Engine'],
        rows: [
          ['PT2A', 'Trash', '2"', '530 L/min', 'Honda petrol'],
          ['PT3A', 'Trash', '3"', '1,234 L/min', 'Honda petrol'],
          ['PTS4V', 'Trash', '4"', '2,608 L/min', 'Vanguard petrol'],
          ['PT6Y', 'Trash', '6"', '4,300 L/min', 'Yanmar diesel'],
          ['PDT2A', 'Diaphragm', '2"', '189 L/min', 'Honda petrol'],
          ['PDT3A', 'Diaphragm', '3"', '333 L/min', 'Honda petrol'],
          ['PG2A', 'Dewatering', '2"', '640 L/min', 'Honda petrol'],
          ['PG3A', 'Dewatering', '3"', '1,098 L/min', 'Honda petrol'],
        ],
      },
      docs: [{ label: 'Pump line sheet', href: '/line-sheets/wacker-neuson-pumps.pdf' }],
      h1Lead: 'Water Pumps in',
      h1Highlight: 'Guyana',
      subtitle:
        'Self-priming trash, diaphragm and dewatering pumps for water transfer and mining-site duty — supplied to order by GIS, delivered and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson self-priming engine-driven pumps across Guyana for dewatering, water transfer and mining-site duty. The PT trash-pump range handles solids-laden water in 2", 3", 4" and 6" sizes (the 6" PT6Y is diesel, the rest petrol Honda or Vanguard); the PDT diaphragm pumps move mud and slurries that would stall a centrifugal pump; and the PG dewatering pumps are the right call for clean-water removal. Tell us the medium, the flow and the head, and we will match the right pump and source it to order with local support.',
      seoTitle: 'Water Pumps in Guyana — Trash, Dewatering & Diaphragm PT/PDT/PG | Goldman Industrial Solutions',
      seoDescription:
        'Trash, dewatering and diaphragm pumps in Guyana through Goldman Industrial Solutions — Wacker Neuson PT, PDT and PG series for mining and construction, sourced to order. Request a quote.',
      tileModelChip: 'PT · PDT · PG series',
      tileBadges: ['Up to 4,300 L/min', '2"–6"', 'Honda/Vanguard/Yanmar'],
      related: ['submersible-pumps'],
    },
    {
      brand: 'wacker-neuson',
      slug: 'submersible-pumps',
      name: 'Submersible Pumps',
      shortName: 'Submersible Pumps',
      blurb: 'Electric submersible pumps for pit drainage, sumps and flooded working areas.',
      image: 'wacker-neuson-submersible-pump.webp',
      photo: true,
      features: [
        'Discharge 170–492 L/min; head up to 26 m',
        'Electric drive — 110 V, 220 V or 110/220 V, 60 Hz',
        '11 models — from 0.75" compact units to 3" high-flow',
        'Built for continuous pit and sump drainage',
      ],
      specTable: {
        head: ['Model', 'Discharge', 'Max head', 'Voltage'],
        rows: [
          ['PSR1500', '170 L/min', '12.0 m', '110 V'],
          ['PSG2500', '236 L/min', '12.0 m', '110 V'],
          ['PST2400 / PSTF2400', '200 L/min', '12.0 m', '110 V'],
          ['PST3750 / PSTF3750', '227 L/min', '18.9 m', '110/220 V'],
          ['PS2500', '236 L/min', '12.0 m', '110/220 V'],
          ['PSA2500', '236 L/min', '12.0 m', '110 V'],
          ['PS2800', '310 L/min', '18.0 m', '110/220 V'],
          ['PSA2800', '310 L/min', '18.0 m', '220 V'],
          ['PS31500', '420 L/min', '21.0 m', '110/220 V'],
          ['PSW31500', '420 L/min', '21.0 m', '110/220 V'],
          ['PS32200', '492 L/min', '25.9 m', '220 V'],
        ],
      },
      docs: [{ label: 'Submersible Pump line sheet', href: '/line-sheets/wacker-neuson-submersible-pumps.pdf' }],
      h1Lead: 'Submersible Pumps in',
      h1Highlight: 'Guyana',
      subtitle:
        'Electric submersible pumps for pit drainage, sumps and flooded working areas — 11 models from 170 to 492 L/min, supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Wacker Neuson electric submersible pumps across Guyana for pit drainage, sumps and flooded working areas. The 11-model PS / PSA / PST range runs from the 0.75" PSR1500 (170 L/min) up to the 3" PS32200 (492 L/min, 25.9 m head), with voltages covering 110 V, 220 V and 110/220 V sites. They are built for continuous duty, with built-in thermal protection and an oil-filled seal. Tell us the pit depth, the flow you need and the supply voltage, and we will match the right unit and source it to order with local support.',
      seoTitle: 'Submersible Pumps in Guyana — Electric PS / PSA / PST 11 Models | Goldman Industrial Solutions',
      seoDescription:
        'Electric submersible pumps in Guyana through Goldman Industrial Solutions — Wacker Neuson PS / PSA / PST range, 11 models from 170 to 492 L/min, sourced to order with delivery and support. Request a quote.',
      tileModelChip: '11 models',
      tileBadges: ['170–492 L/min', 'Electric', 'Up to 26 m head'],
      related: ['water-pumps'],
    },
  ],
  'hyundai-everdigm': [
    {
      brand: 'hyundai-everdigm',
      slug: 'hydraulic-breakers',
      name: 'Hydraulic Breakers',
      shortName: 'Hydraulic Breakers',
      blurb: 'Everdigm’s core line — the EHB series of rock and concrete breakers for excavator carriers.',
      image: 'hyundai-everdigm-breaker.webp',
      features: [
        'EHB range covers carriers from 0.6 to 140 tonnes',
        '20–30 tonne class is our priority fit (EHB20/25/30)',
        'Dual-speed control, anti-blank-blow, auto stop/start',
        'High-pressure accumulator on EHB17 and larger',
      ],
      specTable: {
        head: ['Model', 'Carrier class', 'Oil flow', 'Blow rate'],
        rows: [
          ['EHB17', '14–24 t', '110–170 lpm', '500–750 bpm'],
          ['EHB20', '16–25 t', '100–150 lpm', '440–700 bpm'],
          ['EHB25', '20–30 t', '130–180 lpm', '430–650 bpm'],
          ['EHB30', '26–35 t', '170–240 lpm', '400–680 bpm'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Hydraulic Breakers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm EHB rock and concrete breakers for excavator carriers — sized to your machine, with the 20–30 tonne class as our priority fit. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm hydraulic breakers across Guyana for rock breaking, concrete demolition and trenching in hard ground. The EHB series covers carriers from 0.6 to 140 tonnes, with the 20–30 tonne class — EHB20, EHB25 and EHB30 — our initial priority for the Guyanese market, where it matches the most common carrier size on mining and earthmoving sites. Features like dual-speed control, anti-blank-blow and a high-pressure accumulator on EHB17 and larger keep productivity up and operating costs down. Tell us your excavator make, model and operating weight, and we will match the right breaker and quote it with wear parts.',
      seoTitle: 'Hydraulic Breakers in Guyana — Everdigm EHB for 20–30 t Carriers | Goldman Industrial Solutions',
      seoDescription:
        'Hydraulic breakers in Guyana through Goldman Industrial Solutions — Hyundai Everdigm EHB series for excavator carriers (20–30 t priority), sourced to order with delivery and support. Request a quote.',
      tileModelChip: 'EHB17–EHB30',
      tileBadges: ['14–35 t carriers', 'Dual-speed', '20–30 t priority'],
      related: ['crushers', 'pulverisers'],
    },
    {
      brand: 'hyundai-everdigm',
      slug: 'crushers',
      name: 'Crushers',
      shortName: 'Crushers',
      blurb: 'Concrete and demolition crushers for primary and secondary crushing.',
      image: 'hyundai-everdigm-crusher.webp',
      features: [
        'Crushing force 56–85 tonnes at the tip',
        'For demolition, recycling and concrete processing',
        'Rebar-cutting blades',
      ],
      specTable: {
        head: ['Model', 'Carrier class', 'Crushing force', 'Oil flow'],
        rows: [
          ['EHP16', '13–20 t', '56 t', '100–180 lpm'],
          ['EHP19', '16–25 t', '59 t', '150–250 lpm'],
          ['EHP22', '20–28 t', '70 t', '150–250 lpm'],
          ['EHP32', '28–36 t', '85 t', '200–300 lpm'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Crushers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm concrete and demolition crushers for primary and secondary crushing — sized to your excavator, with rebar-cutting blades. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm crusher attachments across Guyana for primary and secondary concrete crushing on demolition and recycling jobs. The EHP16 to EHP32 range covers carriers from 13 to 36 tonnes, with crushing forces from 56 to 85 tonnes at the tip and rebar-cutting blades that separate steel from concrete as you work. The 20–30 tonne carrier class is our initial priority fit. Tell us your carrier and the material you are processing, and we will match the right crusher and source it to order.',
      seoTitle: 'Concrete & Demolition Crushers in Guyana — Everdigm EHP16–EHP32 | Goldman Industrial Solutions',
      seoDescription:
        'Concrete and demolition crushers in Guyana through Goldman Industrial Solutions — Hyundai Everdigm EHP16 to EHP32 for 13–36 t carriers, sourced to order. Request a quote.',
      tileModelChip: 'EHP16–EHP32',
      tileBadges: ['13–36 t carriers', '56–85 t force', 'Rebar cutting'],
      related: ['hydraulic-breakers', 'pulverisers'],
    },
    {
      brand: 'hyundai-everdigm',
      slug: 'pulverisers',
      name: 'Pulverisers',
      shortName: 'Pulverisers',
      blurb: 'Secondary pulverisers for concrete processing and rebar separation.',
      image: 'hyundai-everdigm-pulveriser.webp',
      features: [
        'Efficient concrete reduction',
        'Separates rebar for recycling',
        '360° rotating options',
      ],
      specTable: {
        head: ['Model (rotatable)', 'Carrier class', 'Crushing force', 'Weight'],
        rows: [
          ['EHP16', '13–16 t', '56 t', '1,250 kg'],
          ['EHP20', '17–25 t', '61 t', '1,817 kg'],
          ['EHP25', '20–27 t', '73 t', '2,517 kg'],
          ['EHP35', '28–36 t', '85 t', '3,017 kg'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Pulverisers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm secondary pulverisers for concrete reduction and rebar separation — for demolition and recycling. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm pulveriser attachments across Guyana for secondary concrete processing — reducing broken concrete to a manageable size and separating the rebar for clean recycling. The 360° rotating options make precise placement easy for the operator. Pair them with a hydraulic breaker or crusher on the same job for a complete demolition flow. Tell us your carrier and your processing target, and we will match the right pulveriser and source it to order with wear parts.',
      seoTitle: 'Pulverisers in Guyana — Everdigm Secondary Concrete Pulverisers | Goldman Industrial Solutions',
      seoDescription:
        'Secondary pulverisers in Guyana through Goldman Industrial Solutions — Hyundai Everdigm concrete pulverisers with rebar separation and 360° rotation, sourced to order. Request a quote.',
      tileModelChip: 'Secondary pulverisers',
      tileBadges: ['Concrete + rebar', '360° rotating', 'Demolition'],
      related: ['crushers', 'grabs-grapples'],
    },
    {
      brand: 'hyundai-everdigm',
      slug: 'grabs-grapples',
      name: 'Grabs & Grapples',
      shortName: 'Grabs & Grapples',
      blurb: 'Selector and sorting grabs for demolition, scrap and material handling.',
      image: 'hyundai-everdigm-grab.webp',
      features: [
        'Demolition and sorting duty',
        'Robust tine construction',
        'Rotating versions available',
      ],
      specTable: {
        head: ['Model', 'Carrier class', 'Weight', 'Max closing force'],
        rows: [
          ['EPG05A', '4–8 t', '385 kg', '2.2 t'],
          ['EPG08A', '7–10 t', '530 kg', '3.0 t'],
          ['EPG14', '11–16 t', '1,086 kg', '6.1 t'],
          ['EPG20', '18–25 t', '1,494 kg', '7.3 t'],
          ['EPG30', '25–38 t', '2,370 kg', '8.6 t'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Selector Grabs & Grapples in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm selector and sorting grabs for demolition, scrap and material handling — sized to your excavator. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm selector grabs and grapples across Guyana for demolition sorting, scrap handling and material processing on site. Robust tine construction handles rebar-loaded debris, and rotating versions let the operator place material precisely without repositioning the carrier. Tell us your excavator and the material you are handling, and we will match the right grab and source it to order.',
      seoTitle: 'Selector Grabs & Grapples in Guyana — Everdigm Sorting Grabs | Goldman Industrial Solutions',
      seoDescription:
        'Selector and sorting grabs in Guyana through Goldman Industrial Solutions — Hyundai Everdigm demolition and scrap grapples, sized to your excavator, sourced to order. Request a quote.',
      tileModelChip: 'EPG05A–EPG30',
      tileBadges: ['Demolition', 'Sorting', 'Rotating'],
      related: ['pulverisers', 'quick-couplers'],
    },
    {
      brand: 'hyundai-everdigm',
      slug: 'quick-couplers',
      name: 'Quick Couplers',
      shortName: 'Quick Couplers',
      blurb: 'Couplers for fast, safe attachment changes on the carrier.',
      image: 'hyundai-everdigm-quick-coupler.webp',
      features: [
        'Quick attachment swaps',
        'Improves machine utilisation',
        'Carrier-matched',
      ],
      specTable: {
        head: ['Model (EQD full-auto)', 'Weight', 'Mountable pin-to-pin'],
        rows: [
          ['EQD05', '74–87 kg', '217–310 mm'],
          ['EQD08', '103–120 kg', '270–380 mm'],
          ['EQD14', '170–206 kg', '355–432 mm'],
          ['EQD22', '349–390 kg', '427–525 mm'],
          ['EQD30', '551–580 kg', '455–591 mm'],
          ['EQD45', '740–892 kg', '550–640 mm'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Quick Couplers in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm quick couplers for fast, safe attachment changes on the carrier — carrier-matched and built for site duty. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm quick couplers across Guyana for excavators that swap between breakers, crushers, grabs and buckets through the day. A coupler turns a manual attachment change from a 20-minute yard job into a 30-second in-cab operation, which materially lifts machine utilisation on mixed-duty sites. Tell us your carrier’s pin dimensions and hydraulic circuit, and we will match the right coupler and source it to order.',
      seoTitle: 'Quick Couplers in Guyana — Everdigm Excavator Couplers | Goldman Industrial Solutions',
      seoDescription:
        'Quick couplers in Guyana through Goldman Industrial Solutions — Hyundai Everdigm couplers for fast attachment changes, sized to your excavator, sourced to order. Request a quote.',
      tileModelChip: 'EQD03–EQD70',
      tileBadges: ['Fast swaps', 'Carrier-matched', 'Safe changes'],
      related: ['grabs-grapples', 'compactors'],
    },
    {
      brand: 'hyundai-everdigm',
      slug: 'compactors',
      name: 'Compactors',
      shortName: 'Compactors',
      blurb: 'Excavator-mounted plate compactors for trenches and slopes.',
      image: 'hyundai-everdigm-compactor.webp',
      features: [
        'Trench and embankment compaction',
        'Vibratory action',
        'Reaches where rollers cannot',
      ],
      specTable: {
        head: ['Model', 'Impulse force', 'Weight', 'Oil flow'],
        rows: [
          ['EPC05', '2.1 t', '260–280 kg', '45–65 lpm'],
          ['EPC10', '4.0 t', '430 kg', '50–120 lpm'],
          ['EPC15', '6.4 t', '689 kg', '80–170 lpm'],
          ['EPC20', '11.2 t', '1,094 kg', '110–200 lpm'],
          ['EPC30', '17.3 t', '1,502 kg', '160–250 lpm'],
        ],
      },
      docs: [{ label: 'Attachment catalogue', href: '/line-sheets/hyundai-everdigm-attachment-catalogue.pdf' }],
      h1Lead: 'Excavator-Mounted Compactors in',
      h1Highlight: 'Guyana',
      subtitle:
        'Hyundai Everdigm excavator-mounted plate compactors for trenches and slopes — vibratory action that reaches where rollers cannot. Supplied to order by GIS and supported in Guyana.',
      intro:
        'GIS supplies Hyundai Everdigm excavator-mounted plate compactors across Guyana for trench backfill, embankment work and slope compaction — places a ride-on roller cannot reach. The vibratory plate uses the carrier’s hydraulics, so there is no separate engine to maintain, and the operator can compact the trench they just dug without leaving the cab. Tell us your excavator and the trench width you typically work, and we will match the right plate and source it to order.',
      seoTitle: 'Excavator Plate Compactors in Guyana — Everdigm Vibratory | Goldman Industrial Solutions',
      seoDescription:
        'Excavator-mounted plate compactors in Guyana through Goldman Industrial Solutions — Hyundai Everdigm vibratory plates for trench and slope compaction, sourced to order. Request a quote.',
      tileModelChip: 'EPC05–EPC30',
      tileBadges: ['Trench compaction', 'Vibratory', 'Slope work'],
      related: ['quick-couplers', 'hydraulic-breakers'],
    },
  ],
};

// ---------- Lookup helpers ----------

const PRODUCT_INDEX: Map<string, Product> = (() => {
  const m = new Map<string, Product>();
  (Object.keys(PRODUCTS) as BrandSlug[]).forEach((brand) => {
    PRODUCTS[brand].forEach((p) => m.set(`${brand}/${p.slug}`, p));
  });
  return m;
})();

export function getProduct(brand: string, slug: string): Product | undefined {
  return PRODUCT_INDEX.get(`${brand}/${slug}`);
}

export function getAllProducts(): Product[] {
  return (Object.keys(PRODUCTS) as BrandSlug[]).flatMap((b) => PRODUCTS[b]);
}

export function getAllProductRoutes(): { brand: BrandSlug; slug: string }[] {
  return (Object.keys(PRODUCTS) as BrandSlug[]).flatMap((brand) =>
    PRODUCTS[brand].map((p) => ({ brand, slug: p.slug })),
  );
}
